import { lazy, Suspense } from 'react';
import type { ReactElement } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { RequireAuth } from './lib/RequireAuth';
import { useTimesheetVisibility } from './lib/useTimesheetVisibility';
import { Home } from './pages/Home';
import { Layout } from './pages/Layout';
import { Login } from './pages/Login';
import { ForceSignOutHandler } from './pages/settings/access-levels/ForceSignOutHandler';

// Route-level code splitting: every page below loads as its own chunk on first visit, so the
// initial bundle stays free of the heavy leaf dependencies (pdf.js in the document viewers,
// TipTap in Email Alerts, the signature stack in Files). Login, Layout and Home stay eager -
// they are the guaranteed first paint. Pages export named components, so each lazy() maps the
// named export onto the default-export shape React.lazy requires (a shared helper would erase
// per-component prop types, so the mapping stays inline).
const Approvals = lazy(() =>
  import('./pages/Approvals').then((m) => ({ default: m.Approvals })));
const Inbox = lazy(() =>
  import('./pages/inbox/Inbox').then((m) => ({ default: m.Inbox })));
const Calendar = lazy(() =>
  import('./pages/calendar/Calendar').then((m) => ({ default: m.Calendar })));
const Files = lazy(() =>
  import('./pages/Files').then((m) => ({ default: m.Files })));
const FileView = lazy(() =>
  import('./pages/FileView').then((m) => ({ default: m.FileView })));
const SignDocument = lazy(() =>
  import('./pages/SignDocument').then((m) => ({ default: m.SignDocument })));
const SignedDocumentView = lazy(() =>
  import('./pages/SignedDocumentView').then((m) => ({ default: m.SignedDocumentView })));
const ProfileShell = lazy(() =>
  import('./pages/my-info/ProfileShell').then((m) => ({ default: m.ProfileShell })));
const NotFound = lazy(() =>
  import('./pages/NotFound').then((m) => ({ default: m.NotFound })));
const PeopleShell = lazy(() =>
  import('./pages/people/PeopleShell').then((m) => ({ default: m.PeopleShell })));
const AccessLevelsPage = lazy(() =>
  import('./pages/settings/access-levels/AccessLevelsPage')
    .then((m) => ({ default: m.AccessLevelsPage })));
const ApprovalsPage = lazy(() =>
  import('./pages/settings/approvals/ApprovalsPage').then((m) => ({ default: m.ApprovalsPage })));
const CompanyDirectoryPage = lazy(() =>
  import('./pages/settings/company-directory/CompanyDirectoryPage')
    .then((m) => ({ default: m.CompanyDirectoryPage })));
const EmailAlertsPage = lazy(() =>
  import('./pages/settings/email-alerts/EmailAlertsPage')
    .then((m) => ({ default: m.EmailAlertsPage })));
const EmployeeFieldsPage = lazy(() =>
  import('./pages/settings/employee-fields/EmployeeFieldsPage')
    .then((m) => ({ default: m.EmployeeFieldsPage })));
const HolidaysPage = lazy(() =>
  import('./pages/settings/holidays/HolidaysPage').then((m) => ({ default: m.HolidaysPage })));
const OffboardingPage = lazy(() =>
  import('./pages/settings/offboarding/OffboardingPage')
    .then((m) => ({ default: m.OffboardingPage })));
const OnboardingPage = lazy(() =>
  import('./pages/settings/onboarding/OnboardingPage')
    .then((m) => ({ default: m.OnboardingPage })));
const PerformancePage = lazy(() =>
  import('./pages/settings/performance/PerformancePage')
    .then((m) => ({ default: m.PerformancePage })));
const SettingsLayout = lazy(() =>
  import('./pages/settings/SettingsLayout').then((m) => ({ default: m.SettingsLayout })));
const SectionPlaceholder = lazy(() =>
  import('./pages/settings/SettingsLayout').then((m) => ({ default: m.SectionPlaceholder })));
const SettingsIndexRedirect = lazy(() =>
  import('./pages/settings/SettingsLayout').then((m) => ({ default: m.SettingsIndexRedirect })));
const TimeOffPage = lazy(() =>
  import('./pages/settings/time-off/TimeOffPage').then((m) => ({ default: m.TimeOffPage })));
const TimesheetSettingsPage = lazy(() =>
  import('./pages/settings/timesheet/TimesheetSettingsPage')
    .then((m) => ({ default: m.TimesheetSettingsPage })));
const ReportingPage = lazy(() =>
  import('./pages/settings/reporting/ReportingPage')
    .then((m) => ({ default: m.ReportingPage })));
const TimesheetApprovalsPage = lazy(() =>
  import('./pages/timesheet/TimesheetApprovalsPage')
    .then((m) => ({ default: m.TimesheetApprovalsPage })));
const TimesheetPage = lazy(() =>
  import('./pages/timesheet/TimesheetPage').then((m) => ({ default: m.TimesheetPage })));
const TimesheetPeoplePage = lazy(() =>
  import('./pages/timesheet/TimesheetPeoplePage')
    .then((m) => ({ default: m.TimesheetPeoplePage })));
const TimesheetMonthStatusPage = lazy(() =>
  import('./pages/timesheet/TimesheetMonthStatusPage')
    .then((m) => ({ default: m.TimesheetMonthStatusPage })));
const ProjectTimesheetsPage = lazy(() =>
  import('./pages/timesheet/ProjectTimesheetsPage')
    .then((m) => ({ default: m.ProjectTimesheetsPage })));

// Gate for the Timesheet routes: when the feature is not visible to this user (OFF for everyone,
// ADMIN_ONLY for non-admins) a direct/bookmarked URL redirects home instead of rendering a page
// whose API calls would 403. Renders nothing while the flag loads to avoid a redirect flash.
function RequireTimesheet({ children }: { children: ReactElement }) {
  const visibility = useTimesheetVisibility();
  if (visibility.isLoading) {
    return null;
  }
  if (!visibility.data?.canView) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function PeopleIdRedirect() {
  const { id } = useParams<{ id: string }>();
  // Preserve any tab hash (and search) so deep links like /people/64#job survive the redirect.
  const { search, hash } = useLocation();
  return <Navigate to={`/employees/${id ?? ''}${search}${hash}`} replace />;
}

// /people resolves to the Directory tab; preserve ?new=1 (and any other search) so the TopBar
// "+New" button still opens the Add Employee drawer after the redirect.
function PeopleIndexRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/people/directory${search}`} replace />;
}

// Legacy standalone /org-chart now lives under the People page; preserve ?focus= for deep links.
function OrgChartRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/people/org-chart${search}`} replace />;
}

// Preserves ?tab= and hash so inbox deep links like /my-info?tab=time-off land on the right tab.
function MyInfoRedirect() {
  const { search, hash } = useLocation();
  return <Navigate to={`/me${search}${hash}`} replace />;
}

export default function App() {
  return (
    <>
      {/* Every 401 redirects to /login from here; RequireAuth leaves its own 401 to this handler,
          so keep it mounted for the whole app. */}
      <ForceSignOutHandler />
      {/* Outermost boundary: covers lazy pages rendered outside Layout (NotFound). Pages inside
          Layout suspend on the boundary around Layout's Outlet, keeping the shell visible. */}
      <Suspense fallback={<section className="p-8 text-sm text-slate-500">Loading…</section>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route element={<RequireAuth />}>
            <Route element={<Layout />}>
              <Route path="/" element={<Home />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/me" element={<ProfileShell />} />
              <Route path="/my-info" element={<MyInfoRedirect />} />
              <Route path="/people" element={<PeopleIndexRedirect />} />
              <Route path="/people/directory" element={<PeopleShell tab="directory" />} />
              <Route path="/people/list" element={<PeopleShell tab="list" />} />
              <Route path="/people/org-chart" element={<PeopleShell tab="org-chart" />} />
              <Route path="/people/:id" element={<PeopleIdRedirect />} />
              <Route path="/employees/:id" element={<ProfileShell />} />
              {/* Document viewers scoped under a profile path so the left-nav stays on My Info / the
                  profile instead of jumping to Files when a doc is opened from a Documents tab. */}
              <Route path="/me/documents/:id" element={<FileView />} />
              <Route path="/me/signed/:documentId" element={<SignedDocumentView />} />
              <Route path="/employees/:eid/documents/:id" element={<FileView />} />
              <Route path="/employees/:eid/signed/:documentId" element={<SignedDocumentView />} />
              <Route path="/directory" element={<Navigate to="/people/directory" replace />} />
              <Route path="/org-chart" element={<OrgChartRedirect />} />
              <Route path="/files" element={<Files />} />
              <Route path="/files/:id" element={<FileView />} />
              <Route path="/sign/:documentId" element={<SignDocument />} />
              <Route path="/signed/:documentId" element={<SignedDocumentView />} />
              <Route path="/approvals" element={<Approvals />} />
              <Route
                path="/timesheet"
                element={
                  <RequireTimesheet>
                    <TimesheetPage />
                  </RequireTimesheet>
                }
              />
              <Route
                path="/timesheet/projects"
                element={
                  <RequireTimesheet>
                    <ProjectTimesheetsPage />
                  </RequireTimesheet>
                }
              />
              <Route
                path="/timesheet/month"
                element={
                  <RequireTimesheet>
                    <TimesheetMonthStatusPage />
                  </RequireTimesheet>
                }
              />
              <Route
                path="/timesheet/people"
                element={
                  <RequireTimesheet>
                    <TimesheetPeoplePage />
                  </RequireTimesheet>
                }
              />
              <Route
                path="/timesheet/approvals"
                element={
                  <RequireTimesheet>
                    <TimesheetApprovalsPage />
                  </RequireTimesheet>
                }
              />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/settings" element={<SettingsLayout />}>
                <Route index element={<SettingsIndexRedirect />} />
                {/* General held only the Timesheet toggle, now folded into Settings > Timesheet. */}
                <Route path="general" element={<Navigate to="/settings/timesheet" replace />} />
                <Route path="access-levels" element={<AccessLevelsPage />} />
                <Route path="access-levels/:subId" element={<AccessLevelsPage />} />
                <Route path="access-levels/:subId/history" element={<AccessLevelsPage />} />
                <Route path="employee-fields" element={<EmployeeFieldsPage />} />
                <Route path="approvals" element={<ApprovalsPage />} />
                <Route path="company-directory" element={<CompanyDirectoryPage />} />
                <Route
                  path="custom-fields"
                  element={
                    <SectionPlaceholder name="Custom Fields & Tables — planned for a future phase" />
                  }
                />
                <Route path="email-alerts" element={<EmailAlertsPage />} />
                <Route path="holidays" element={<HolidaysPage />} />
                <Route path="onboarding" element={<OnboardingPage />} />
                <Route path="offboarding" element={<OffboardingPage />} />
                <Route path="performance" element={<PerformancePage />} />
                <Route path="time-off" element={<TimeOffPage />} />
                <Route path="reporting" element={<ReportingPage />} />
                {/* No RequireTimesheet here: the hub hosts the on/off toggle, so an admin must be
                    able to reach it even when the feature is OFF. Admin-gating is done by SettingsLayout. */}
                <Route path="timesheet" element={<TimesheetSettingsPage />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
}
